import * as React from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { cn } from "../../lib/utils.js";

/**
 * Combobox — UN solo campo que busca y elige.
 *
 * Reemplaza al par "input de búsqueda + `<select>` filtrado", que obliga a
 * escribir en un control y elegir en OTRO: el operador teclea, no ve pasar
 * nada, y recién al abrir el desplegable descubre si su búsqueda pegó. Acá se
 * escribe y se elige en el mismo lugar, con la lista debajo respondiendo tecla
 * a tecla.
 *
 * Decisiones que no son cosméticas:
 *
 *  - **El texto de búsqueda vive ACÁ.** En el patrón viejo el estado estaba en
 *    la pantalla, así que cada tecla re-renderizaba el formulario entero (en el
 *    panel de entrega: 19 ítems de checklist, 76 botones y el subidor de
 *    fotos) y hacía falta un debounce para que no se sintiera pastoso. Con el
 *    estado adentro, teclear solo re-pinta esta lista: no hay debounce que
 *    calibrar ni retardo que explicar.
 *
 *  - **La lista va en un portal `fixed`.** Estos campos viven dentro de
 *    `SlidePanel`, cuyo cuerpo tiene `overflow-y-auto`: en flujo normal el
 *    desplegable se recorta contra el borde inferior del panel. Mismo criterio
 *    (y misma medición) que `DatePicker`.
 *
 *  - **Se busca sin tildes y por dígitos sueltos.** En el patio la cédula se
 *    dicta con puntos y el nombre se teclea sin tildes: "gomez" encuentra a
 *    Gómez y "1.001" encuentra al 1001.
 *
 * @param {object} props
 * @param {string} [props.id]                 id del `<input>` — para `htmlFor` de `Field`.
 * @param {string} props.value                valor elegido ("" = ninguno).
 * @param {(value: string, option: object|null) => void} props.onChange
 * @param {Array<{value: string, label: string, description?: string, note?: string,
 *   keywords?: string[], disabled?: boolean}>} props.options
 *   `description` es la segunda línea (cédula, teléfono); `note` la marca a la
 *   derecha (p. ej. "tiene XYZ789"). Las dos se buscan, igual que `keywords`.
 * @param {string} [props.placeholder]
 * @param {string} [props.emptyText]          qué decir cuando el filtro no pega.
 * @param {string} [props.listLabel]          nombre accesible de la lista.
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.clearable=true]    botón ✕ para volver a "ninguno".
 * @param {boolean} [props.error]
 * @param {(query: string) => void} [props.onCreate]
 *   Salida cuando lo buscado no existe: agrega al final de la lista una fila
 *   para crearlo con el texto tecleado. Sin esto, buscar algo que no está
 *   termina en "sin resultados" y en cerrar el panel.
 * @param {string} [props.createLabel="Crear"]  Verbo de esa fila.
 */

/** Rango de marcas diacríticas combinantes — lo que NFD separa de la letra. */
const DIACRITICOS = /[\u0300-\u036f]/g;

/** Minúsculas y sin tildes: "Gómez" y "gomez" tienen que ser lo mismo. */
function norm(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(DIACRITICOS, "")
    .toLowerCase();
}

/** Solo los dígitos — para que "1.001" y "300 123 4567" se busquen escritos de cualquier forma. */
function digitsOf(value) {
  return String(value ?? "").replace(/[^0-9]+/g, "");
}

export function Combobox({
  id,
  value,
  onChange,
  options = [],
  placeholder = "Busca y elige…",
  emptyText = "Sin resultados",
  listLabel = "Opciones",
  disabled = false,
  clearable = true,
  error = false,
  onCreate,
  createLabel = "Crear",
  className,
  ...props
}) {
  const generatedId = React.useId();
  const inputId = id ?? `combobox-${generatedId}`;
  const listId = `${inputId}-listbox`;

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [pos, setPos] = React.useState({ left: 0, top: 0, width: 0 });

  const wrapRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  const selectedIndex = React.useMemo(() => {
    if (value === null || value === undefined || value === "") return -1;
    return options.findIndex((o) => String(o.value) === String(value));
  }, [options, value]);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  // Un texto por opción, calculado una vez por lista y no en cada tecla.
  const haystacks = React.useMemo(
    () =>
      options.map((o) => {
        const raw = [o.label, o.description, o.note, ...(o.keywords ?? [])]
          .filter(Boolean)
          .join(" ");
        return { text: norm(raw), digits: digitsOf(raw) };
      }),
    [options],
  );

  const filtered = React.useMemo(() => {
    const q = norm(query).trim();
    if (!q) return options;
    const qDigits = digitsOf(q);
    return options.filter((_, i) => {
      const h = haystacks[i];
      if (h.text.includes(q)) return true;
      // Con menos de dos dígitos cualquier cédula "coincide" y el filtro deja
      // de filtrar; ahí manda el texto.
      return qDigits.length >= 2 && h.digits.includes(qDigits);
    });
  }, [options, haystacks, query]);

  // Fila de "crear": el fondo del callejón sin salida. Buscar algo que no está
  // no puede terminar en "no hay resultados" y a cerrar el panel — el conductor
  // nuevo llega al patio con la moto ya cargada. Solo con texto escrito: sin
  // búsqueda no hay nombre que crear, y la fila sobraría en la lista completa.
  const canCreate = Boolean(onCreate) && query.trim() !== "";
  // Índices de navegación: las opciones y, al final, la fila de crear.
  const createIndex = canCreate ? filtered.length : -1;
  const navCount = filtered.length + (canCreate ? 1 : 0);
  const onCreateRow = canCreate && activeIndex === createIndex;
  const activeOption = activeIndex >= 0 ? (filtered[activeIndex] ?? null) : null;

  const openList = React.useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    // Abrir parado sobre lo ya elegido: la flecha abajo mueve DESDE ahí, que es
    // lo que espera quien viene a cambiar una selección, no a estrenarla.
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : options.length ? 0 : -1);
  }, [disabled, options.length, selectedIndex]);

  const close = React.useCallback(() => {
    setOpen(false);
    setQuery("");
    setActiveIndex(-1);
  }, []);

  const pick = React.useCallback(
    (option) => {
      if (!option || option.disabled) return;
      onChange?.(option.value, option);
      close();
      inputRef.current?.focus();
    },
    [onChange, close],
  );

  // Se cierra ANTES de avisar: lo que el caller abre para crear (un formulario
  // debajo del campo) quedaría tapado por el desplegable. El texto se captura
  // antes, porque `close()` limpia la búsqueda.
  const startCreate = React.useCallback(() => {
    const texto = query.trim();
    if (!onCreate || texto === "") return;
    close();
    onCreate(texto);
  }, [onCreate, query, close]);

  // El filtro puede dejar la lista más corta que el índice activo (o vacía):
  // sin esto, Enter elegiría `undefined` y la tecla se sentiría muerta. La fila
  // de crear cuenta como una más, y cuando no queda nada más ES la activa: sin
  // coincidencias, Enter crea.
  React.useEffect(() => {
    if (!open) return;
    setActiveIndex((i) => {
      if (navCount === 0) return -1;
      return Math.min(i < 0 ? 0 : i, navCount - 1);
    });
  }, [navCount, open]);

  // Clic afuera. La lista vive en un portal, así que "afuera" son DOS cajas:
  // el campo y el desplegable.
  React.useEffect(() => {
    if (!open) return undefined;
    function handleOutside(e) {
      if (wrapRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      close();
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, close]);

  // Posición de la lista flotante: medida contra la ventana antes de pintar, y
  // recalculada al redimensionar o scrollear — el campo vive dentro de un
  // contenedor con scroll propio, de ahí la captura.
  React.useLayoutEffect(() => {
    if (!open) return undefined;
    function place() {
      const anchor = wrapRef.current;
      const list = listRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const h = list?.offsetHeight ?? 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const MARGIN = 8;
      const GAP = 4;
      const width = rect.width;
      const left = Math.max(MARGIN, Math.min(rect.left, vw - width - MARGIN));
      let top = rect.bottom + GAP;
      // No cabe abajo: se abre hacia arriba. En `fixed` no hay scroll que
      // rescate una lista que se salió de la pantalla.
      if (h && top + h > vh - MARGIN) {
        const above = rect.top - GAP - h;
        top = above >= MARGIN ? above : Math.max(MARGIN, vh - h - MARGIN);
      }
      setPos((prev) =>
        prev.left === left && prev.top === top && prev.width === width
          ? prev
          : { left, top, width },
      );
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    // La lista cambia de alto sola mientras se teclea: de 12 opciones a 1.
    const ro =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => place());
    if (ro && listRef.current) ro.observe(listRef.current);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      ro?.disconnect();
    };
  }, [open]);

  // Con teclado, la opción activa tiene que verse: navegar a ciegas por una
  // lista con scroll es peor que no tener teclado.
  React.useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = document.getElementById(`${listId}-option-${activeIndex}`);
    if (typeof el?.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, listId]);

  function handleKeyDown(e) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        openList();
        return;
      }
      if (navCount === 0) return;
      const dir = e.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((i) => {
        const from = i < 0 ? (dir === 1 ? -1 : 0) : i;
        return (from + dir + navCount) % navCount;
      });
      return;
    }
    if (e.key === "Enter") {
      if (open && (activeOption || onCreateRow)) {
        // Sin esto, dentro de un formulario Enter lo enviaría en vez de elegir.
        e.preventDefault();
        if (onCreateRow) startCreate();
        else pick(activeOption);
      }
      return;
    }
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        // Escape acá cierra la lista, no el panel que la contiene.
        e.stopPropagation();
        close();
      }
      return;
    }
    if (e.key === "Tab") {
      if (open) close();
      return;
    }
    if (open && navCount > 0 && (e.key === "Home" || e.key === "End")) {
      e.preventDefault();
      setActiveIndex(e.key === "Home" ? 0 : navCount - 1);
    }
  }

  const showClear = clearable && !disabled && Boolean(selected);
  const filtering = query.trim() !== "";

  const list =
    open &&
    createPortal(
      <div
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={listLabel}
        // Elegir con el mouse no debe sacarle el foco al input: si se lo saca,
        // el `blur` cierra la lista ANTES de que el clic llegue a la opción.
        onMouseDown={(e) => e.preventDefault()}
        style={{ left: pos.left, top: pos.top, width: pos.width || undefined }}
        className={cn(
          "fixed z-[330] max-h-64 overflow-y-auto py-1",
          "rounded-xl border border-border bg-background-card shadow-elevated",
        )}
      >
        {filtering && (
          <p className="sticky top-0 z-10 bg-background-card px-3 py-1 text-2xs text-text-muted">
            {filtered.length} de {options.length}
          </p>
        )}

        {filtered.length === 0 ? (
          <p className="px-3 py-3 text-center text-xs text-text-muted">{emptyText}</p>
        ) : (
          filtered.map((o, i) => {
            const isSelected = selectedIndex >= 0 && options[selectedIndex] === o;
            const isActive = i === activeIndex;
            return (
              <div
                key={o.value}
                id={`${listId}-option-${i}`}
                role="option"
                aria-selected={isSelected}
                aria-disabled={o.disabled || undefined}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => pick(o)}
                className={cn(
                  "flex cursor-pointer items-start gap-2 px-3 py-2 text-sm",
                  isActive ? "bg-primary/10" : "",
                  o.disabled ? "cursor-not-allowed opacity-50" : "",
                )}
              >
                <Check
                  aria-hidden="true"
                  className={cn(
                    "mt-0.5 h-4 w-4 shrink-0",
                    isSelected ? "text-primary" : "opacity-0",
                  )}
                  strokeWidth={2}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-text-primary">{o.label}</span>
                  {o.description ? (
                    <span className="block truncate text-xs text-text-muted">
                      {o.description}
                    </span>
                  ) : null}
                </span>
                {o.note ? (
                  <span className="mt-0.5 shrink-0 text-2xs text-warning-soft">{o.note}</span>
                ) : null}
              </div>
            );
          })
        )}

        {/* El fondo del callejón: si lo que se buscó no está, se crea desde
            acá. Va SIEMPRE al final —también con coincidencias— porque a veces
            hay un homónimo y el que llegó es otro. */}
        {canCreate && (
          <div
            id={`${listId}-option-${createIndex}`}
            role="option"
            aria-selected={false}
            onMouseEnter={() => setActiveIndex(createIndex)}
            onClick={startCreate}
            className={cn(
              "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm font-medium text-primary",
              filtered.length > 0 ? "mt-1 border-t border-border-muted pt-2" : "",
              onCreateRow ? "bg-primary/10" : "",
            )}
          >
            <Plus className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">
              {createLabel} «{query.trim()}»
            </span>
          </div>
        )}
      </div>,
      document.body,
    );

  return (
    <div ref={wrapRef} className={cn("relative", className)}>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          open && activeOption ? `${listId}-option-${activeIndex}` : undefined
        }
        aria-invalid={error || undefined}
        disabled={disabled}
        // Cerrado muestra lo elegido; abierto, lo que se está tecleando. Y con
        // la lista abierta lo elegido pasa al placeholder para que no
        // desaparezca de la vista justo cuando se lo va a reemplazar.
        value={open ? query : (selected?.label ?? "")}
        placeholder={open && selected ? selected.label : placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onFocus={() => {
          if (!open) openList();
        }}
        onClick={() => {
          if (!open) openList();
        }}
        onBlur={close}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex h-10 w-full rounded-md py-2 pl-3 text-sm",
          "bg-surface-1 text-text-primary placeholder:text-text-muted",
          "border shadow-card transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0",
          error
            ? "border-danger focus-visible:ring-danger/40"
            : "border-border-strong focus-visible:border-primary focus-visible:ring-primary/40",
          "disabled:cursor-not-allowed disabled:opacity-50",
          showClear ? "pr-16" : "pr-9",
        )}
        {...props}
      />

      <div className="absolute right-1 top-1 flex items-center gap-0.5">
        {showClear && (
          <button
            type="button"
            aria-label="Limpiar selección"
            title="Limpiar selección"
            // Igual que en la lista: sin esto el `blur` cierra y el clic reabre.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange?.("", null);
              close();
              inputRef.current?.focus();
            }}
            className="grid h-8 w-8 place-items-center rounded-md text-text-muted transition-colors hover:bg-surface-2 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <X className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? "Cerrar la lista" : "Abrir la lista"}
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (open) close();
            else {
              openList();
              inputRef.current?.focus();
            }
          }}
          className="grid h-8 w-8 place-items-center rounded-md text-text-muted transition-colors hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronDown
            className={cn("h-4 w-4 transition-transform", open ? "rotate-180" : "")}
            strokeWidth={2}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Sin esto el lector de pantalla no se entera de que la lista se movió
          bajo sus pies: se escribe, quedan 2 de 40, y no lo dice nadie. */}
      <span role="status" aria-live="polite" className="sr-only">
        {open && filtering
          ? filtered.length === 1
            ? "1 resultado"
            : `${filtered.length} resultados`
          : ""}
      </span>

      {list}
    </div>
  );
}

Combobox.displayName = "Combobox";
